import type { Form, FormTranslation } from "~/models/Form";
import type {
    ChoiceFieldMetadata,
    FormField,
    TextFieldMetadata,
} from "~/models/FormField";
import type { ApiResponse } from "~/types/ApiResponse";
import type { AsyncDataRequestStatus } from "#app/composables/asyncData";
import type { Form as NuxtForm } from "#ui/types";
import { faSparkles } from "@fortawesome/pro-solid-svg-icons";
import type { ApiErrorResponse } from "~/plugins/errors/apiError";
import type ApiError from "~/plugins/errors/apiError";
import type { GenerateFormOptions } from "~/services/FormService";

const EMPTY_FORM: Form = {
    id: "",
    is_public: false,
    is_active_template: false,
    status: "draft",
    type: "form",
    name: "",
    slug: "",
    description: "",
    locale: {
        id: null,
    },
    brand: {
        id: null,
    },
    metadata: {
        has_customized_logic: false,
        ai_instructions: "",
        fields: [],
        theme: {
            logo: "",
            font: "",
            textColor: "",
            buttonText: "",
            inputColor: "",
            inputVariant: "solid",
            inputBackgroundColor: "",
            buttonColor: "",
            buttonTextColor: "",
            backgroundColor: "",
            backgroundImage: "",
            backgroundOpacity: 1,
            alignment: "center",
            scale: "md",
        },
    },
    fields: [],
    translations: [],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    deleted_at: null,
    preview_url: "",
};

type Metadata = { [key: string]: any };

export interface FormSaveOptions {
    onSuccess?: (form: ApiResponse<Form>) => void;
    onError?: (error: ApiErrorResponse | Error) => void;
    onDone?: () => void;
}

export const useFormStore = defineStore("form-store", () => {
    // Dependencies
    const api = useApi();
    const { hasPermissionTo, user } = useUser();
    const dayjs = useDayjs();

    // State
    const initialForm = shallowRef<Form>(EMPTY_FORM);
    const form = shallowRef<Form>(EMPTY_FORM);
    const brand = shallowRef<Form["brand"]>(EMPTY_FORM.brand);
    const isPublic = ref<boolean>(EMPTY_FORM.is_public);
    const name = ref<string>(EMPTY_FORM.name);
    const locale = ref<Form["locale"]>(EMPTY_FORM.locale);
    const slug = ref<string>(EMPTY_FORM.slug);
    const description = ref<string>(EMPTY_FORM.description ?? "");
    const status = ref<Form["status"]>(EMPTY_FORM.status);
    const metadata = ref<Form["metadata"]>(EMPTY_FORM.metadata);
    const fields = shallowRef<FormField[]>(EMPTY_FORM.fields);
    const lastUpdated = ref(new Date().toISOString());
    const translations = ref<Form["translations"]>(
        EMPTY_FORM.translations ?? [],
    );

    // Builder
    const builder = ref<NuxtForm<Form>>();
    const copiedField = ref<FormField | null>(null);
    const previewLocale = ref<Locale | null>(null);

    // Statuses
    const saving = ref(false);
    const generating = ref(false);
    const loadStatus = ref<AsyncDataRequestStatus>("pending");
    const focusedFieldId = ref<string | null>(null);
    const editingTranslation = ref<FormTranslation | null>(null);
    const savingTranslation = ref<boolean>(false);

    // Data
    const data_source_items = ref<Record<string, any[]>>({});
    const dropdown = ref<null | ReturnType<typeof api.forms.dropdown>>(null);

    const { data: default_fields, refresh: refreshFields } = useAsyncData(
        async () => {
            if (!form.value.id) {
                return [];
            }

            try {
                const data = await api.forms.listFieldMap(
                    form.value.id,
                    form.value ?? {},
                );

                return data.data;
            } catch {
                return [];
            }
        },
        {
            immediate: false,
        },
    );

    async function loadFormById(id: string) {
        loadStatus.value = "pending";
        try {
            const response = await api.forms.find(id, {
                query: {
                    value_map: true,
                },
            });
            form.value = response.data;
            initialForm.value = useCloneDeep(response.data);
            await refreshFields();
            loadStatus.value = "success";
        } catch {
            loadStatus.value = "error";
        }
    }

    async function saveForm(options: FormSaveOptions = {}) {
        if (options instanceof Event) {
            options = {};
        }

        if (builder.value) {
            try {
                await builder.value?.validate();
            } catch (error) {
                options.onError?.(error as Error);
                return;
            }
        }

        saving.value = true;

        try {
            const formToSave = useCloneDeep(form.value);
            
            formToSave.fields = formToSave.fields.map(field => {
                if (field.logic === null) {
                    field.logic = [];
                }

                if (field.data_sources && field.data_sources.length > 0) {
                    return {
                        ...field,
                        options: []
                    };
                }
                return field;
            });

            const response = await api.forms.update(form.value.id, formToSave);
            form.value.updated_at = response.data.updated_at;
            initialForm.value = useCloneDeep(form.value);
            triggerRef(lastUpdatedAtDisplay);
            options.onSuccess?.(response);
            refreshFields();
        } catch (error) {
            options.onError?.(error as ApiError<ApiErrorResponse>);
        } finally {
            saving.value = false;
            options.onDone?.();
        }
    }

    async function generateUpdate(options: GenerateFormOptions) {
        generating.value = true;
        try {
            const response = await api.forms.generateUpdate(
                form.value.id,
                options,
            );

            form.value = response.data;
            initialForm.value = useCloneDeep(form.value);

            useToast().add({
                title: "Form updated",
                description: response.message,
                icon: "i-heroicons-check-circle",
                color: "green",
            });

            await refreshFields();
        } catch (error) {
            console.error(error);
        } finally {
            generating.value = false;
        }
    }

    async function saveTranslation(): Promise<
        ApiResponse<FormTranslation> | ApiError | null
    > {
        if (savingTranslation.value) {
            return null;
        }

        savingTranslation.value = true;

        try {
            const response = await api.forms.updateTranslation(
                form.value.id,
                editingTranslation.value ?? {},
            );

            const index =
                translations.value?.findIndex(
                    (t) => t.id === response.data?.id,
                ) ?? -1;

            if (index !== -1) {
                translations.value![index] = response.data;
            } else {
                translations.value = [
                    ...(translations.value ?? []),
                    response.data,
                ].sort((a, b) => a.locale.name.localeCompare(b.locale.name));
            }

            editingTranslation.value = null;

            return response;
        } catch (err) {
            return err as ApiError;
        } finally {
            savingTranslation.value = false;
        }
    }

    async function deleteTranslation(id: number, callback?: () => void) {
        const toast = useToast();
        toast.add({
            id: `delete-translation-${id}`,
            title: "Delete translation?",
            description: "This action cannot be undone.",
            icon: "i-heroicons-exclamation-triangle",
            color: "red",
            timeout: 0,
            actions: [
                {
                    label: "Delete",
                    color: "red",
                    click: async () => {
                        await api.forms.deleteTranslation(form.value.id, id);
                        editingTranslation.value = null;
                        translations.value =
                            translations.value?.filter((t) => t.id !== id) ??
                            [];
                        toast.remove(`delete-translation-${id}`);
                        callback?.();
                    },
                },
                {
                    label: "Cancel",
                    click: () => {
                        toast.remove(`delete-translation-${id}`);
                    },
                },
            ],
        });
    }

    function addField(field: FormField, atIndex: number = -1) {
        if (atIndex === -1) {
            fields.value = [...fields.value, field];
        } else {
            fields.value.splice(atIndex, 0, field);
        }

        form.value.fields = fields.value;
        focusedFieldId.value = field.id;
        updatePositions();
        lastUpdated.value = new Date().toISOString();
    }

    function updateField(field: FormField) {
        const index = fields.value.findIndex((f) => f.id === field.id);

        if (index !== -1) {
            fields.value[index] = field;
            form.value.fields = fields.value;
            lastUpdated.value = new Date().toISOString();
        }
    }

    function removeField(field: FormField) {
        form.value.fields = form.value.fields.filter((f) => f.id !== field.id);
        fields.value = form.value.fields;
        updatePositions();
        lastUpdated.value = new Date().toISOString();
    }

    function copyField(field: FormField) {
        copiedField.value = useCloneDeep(field);
    }

    function pasteField(index?: number) {
        if (index !== undefined && index > form.value?.fields.length)
            index = undefined;

        if (!copiedField.value) {
            return undefined;
        }

        const field = useCloneDeep(copiedField.value) as FormField;

        field.label = `${field.label ?? ""} (Copy)`;
        field.id = createUuid();

        if (field.options?.length) {
            field.options = field.options.map((option) => {
                option.id = createUuid();

                return option;
            });
        }

        addField(field, index);
        copiedField.value = null;
        return field;
    }

    function updatePositions() {
        let position = 0;
        const inputs: FormField[] = [];
        const endings: FormField[] = [];

        fields.value.forEach((field) => {
            if (field.type === "ending") {
                endings.push(field);
            } else {
                field.position = position++;
                inputs.push(field);
            }
        });

        endings.forEach((field) => {
            field.position = position++;
            inputs.push(field);
        });

        fields.value = inputs;
    }

    function transferMetadata(
        oldMetadata: Metadata,
        newMetadataStructure: Metadata,
    ): Metadata {
        // Function to recursively transfer matching fields
        function transfer(oldObj: Metadata, newObj: Metadata): Metadata {
            const result = useClone(newObj);

            useForEach(newObj, (value, key) => {
                if (
                    typeof value === "object" &&
                    !Array.isArray(value) &&
                    useHas(oldObj, key)
                ) {
                    // Recursively handle nested objects
                    result[key] = transfer(oldObj[key], value);
                } else if (useHas(oldObj, key)) {
                    // Transfer the value if the key exists in the old metadata and it's not an array
                    result[key] = oldObj[key];
                }
            });

            return result;
        }

        return transfer(oldMetadata, newMetadataStructure);
    }

    function hasChanges() {
        const currentForm = useCloneDeep(form.value);
        delete currentForm.translations;
        const currentInitialForm = useCloneDeep(initialForm.value);
        delete currentInitialForm.translations;
        return !isEqual(currentForm, currentInitialForm);
    }

    // Computes
    const previewForm = computed<
        Form & { extra_translations: { powered_by: string } }
    >(() => {
        const previewForm = useCloneDeep({
            ...form.value,
            is_public: isPublic.value,
            name: name.value,
            slug: slug.value,
            description: description.value,
            status: status.value,
            fields: useCloneDeep(fields.value).map((field) => {
                if (field.data_sources?.length) {
                    const options = [];

                    for (const data_source of field.data_sources) {
                        options.push(
                            ...(data_source_items.value[data_source.id] ?? []),
                        );
                    }

                    field.options = options;
                }

                return field;
            }),
            metadata: {
                ...form.value.metadata,
                ...metadata.value,
            },
            locale: previewLocale.value ?? locale.value,
            extra_translations: {
                powered_by: "Powered by",
                upload_title: defaultFileText.upload_title,
                upload_description: defaultFileText.upload_description,
                ...Object.fromEntries(
                    Object.entries(defaultValidationMessages).map(
                        ([key, value]) => [`validation.${key}`, value],
                    ),
                ),
            },
        });

        if (previewForm.locale?.id === locale.value?.id) {
            return previewForm;
        }

        const translation = translations.value?.find(
            (t) => t.locale.id === previewForm.locale?.id,
        );

        if (!translation) {
            return previewForm;
        }

        previewForm.name = translation.name ?? previewForm.name;
        previewForm.slug = translation.slug ?? previewForm.slug;
        previewForm.description =
            translation.description ?? previewForm.description;
        previewForm.metadata.theme.buttonText = (translation.translations?.[
            "metadata.theme.buttonText"
        ] ?? previewForm.metadata.theme.buttonText) as string;
        previewForm.fields = previewForm.fields.map((field: FormField) => {
            field.label = (translation.translations[`${field.id}.label`] ??
                field.label) as string;
            field.description = (translation.translations[
                `${field.id}.description`
            ] ?? field.description) as string;

            if (
                [
                    "text",
                    "email",
                    "phone",
                    "url",
                    "choice",
                    "numeric",
                    "range",
                ].includes(field.type)
            ) {
                (field.metadata as TextFieldMetadata).placeholder = (translation
                    .translations[`${field.id}.placeholder`] ??
                    (field.metadata as TextFieldMetadata)
                        .placeholder) as string;
            }

            if (field.type === "rating") {
                field.metadata.min_label = (translation.translations[
                    `${field.id}.min_label`
                ] ?? field.metadata.min_label) as string;
                field.metadata.mid_label = (translation.translations[
                    `${field.id}.mid_label`
                ] ?? field.metadata.mid_label) as string;
                field.metadata.max_label = (translation.translations[
                    `${field.id}.max_label`
                ] ?? field.metadata.max_label) as string;
            }

            if (["choice", "rating"].includes(field.type)) {
                (
                    field.metadata as ChoiceFieldMetadata
                ).conversationalButtonText = (translation.translations[
                    `${field.id}.conversationalButtonText`
                ] ??
                    (field.metadata as ChoiceFieldMetadata)
                        .conversationalButtonText) as string;
            }

            if (field.type === "ending") {
                field.metadata.button_text = (translation.translations[
                    `${field.id}.button_text`
                ] ?? field.metadata.button_text) as string;
            }

            if (field.type === "choice") {
                field.options =
                    field.options?.map((option) => {
                        option.label = (translation.translations[
                            `${option.id}.label`
                        ] ?? option.label) as string;

                        return option;
                    }) ?? [];
            }

            return field;
        });

        for (const key of Object.keys(previewForm.extra_translations)) {
            previewForm.extra_translations[key] = (translation.translations?.[
                key
            ] ?? previewForm.extra_translations[key]) as string;
        }

        return previewForm;
    });

    const focusedField = computed(() => {
        return fields.value.find((field) => field.id === focusedFieldId.value);
    });

    const builderFields = computed(() => {
        let fields = createFieldCollection(
            (metadata.value.fields ?? []).map((field) => {
                return {
                    ...field,
                    icon: faSparkles,
                    metadata: {
                        ...field.metadata,
                        is_template_field: true,
                    },
                };
            }),
        ).add(...(default_fields.value ?? []));

        if (brand.value?.id) {
            fields = fields.withData(
                brand?.value.value_map
                    ? brand?.value.value_map
                    : {
                          brand: brand.value,
                      },
            );
        } else if (user.value?.account?.brand?.id) {
            fields = fields.withData(
                user.value.account.brand.value_map
                    ? user.value.account.brand.value_map
                    : {
                          brand: user.value.account.brand,
                      },
            );
        }

        return fields.items.map((field) => {
            if (field.key === "brand.website" && !field.default_value) {
                field.default_value = fields.replaceContent(
                    "account.brand.website",
                );
            }
            return field;
        });
    });

    const canManageTemplates = computed(() => {
        return hasPermissionTo("system.templates.manage", {
            level: "account",
            with_super_admin: false,
        });
    });

    const lastUpdatedDisplay = computed(() => {
        return form.value.id && form.value.updated_at
            ? dayjs().to(lastUpdated.value)
            : undefined;
    });

    const lastUpdatedAtDisplay = useShallowComputed(() => {
        return form.value.id && form.value.updated_at
            ? dayjs().to(form.value.updated_at)
            : undefined;
    });

    // Watchers
    watch(form, (newForm: Form) => {
        fields.value = newForm?.fields ?? [];
        name.value = newForm.name;
        locale.value = newForm.locale?.id ? newForm.locale : EMPTY_FORM.locale;
        brand.value = newForm.brand?.id ? newForm.brand : EMPTY_FORM.brand;
        slug.value = newForm.slug;
        description.value = newForm.description ?? "";
        status.value = newForm.status;
        metadata.value = newForm.metadata;
        translations.value = newForm.translations ?? [];
        lastUpdated.value = newForm?.updated_at ?? new Date().toISOString();

        if (!focusedFieldId.value) {
            focusedFieldId.value = newForm?.fields[0]?.id;
        }

        if (import.meta.client) {
            dropdown.value = api.forms.dropdown(newForm, {
                isEditing: true,
            });
        }
    });

    watch(isPublic, (newIsPublic) => {
        form.value.is_public = newIsPublic;
    });

    watch(name, (newName) => {
        form.value.name = newName;
    });

    watch(brand, (newBrand) => {
        form.value.brand = newBrand;
    });

    watch(locale, (newLocale) => {
        form.value.locale = newLocale;
    });

    watch(slug, (newSlug) => {
        form.value.slug = newSlug;
    });

    watch(description, (newDescription) => {
        form.value.description = newDescription;
    });

    watch(translations, (newTranslations) => {
        form.value.translations = newTranslations;
    });

    watch(status, (newStatus) => {
        form.value.status = newStatus;

        saveForm().finally(() => {
            dropdown.value = api.forms.dropdown(form.value, {
                isEditing: true,
            });
        });
    });

    watch(metadata, (newMetadata) => {
        form.value.metadata = newMetadata;

        dropdown.value = api.forms.dropdown(form.value, {
            isEditing: true,
        });
    });

    watch(
        fields,
        async (newFields) => {
            form.value.fields = newFields;
            lastUpdated.value = new Date().toISOString();
            const data_sources = [];

            if (!focusedFieldId.value) {
                focusedFieldId.value = newFields[0]?.id;
            }

            for (const field of newFields) {
                if (field?.data_sources?.length) {
                    data_sources.push(...field.data_sources);
                }
            }

            await Promise.allSettled([
                useUniqBy(data_sources, "id").map(async (source) => {
                    if (!data_source_items.value[source.id]) {
                        const items = await api.data_sources.listItems(
                            source.id,
                        );

                        data_source_items.value[source.id] = items.data;
                    }

                    return;
                }),
            ]);
        },
        {
            deep: true,
        },
    );

    watch([name, isPublic, status, description, metadata, slug], () => {
        lastUpdated.value = new Date().toISOString();
    });

    watch(lastUpdated, () => {
        refreshFields();
    });

    return {
        // Shallow Completed Form
        form,
        // Editable Fields
        isPublic: isPublic,
        name,
        slug,
        description,
        status,
        metadata,
        fields,
        brand,
        translations,
        locale,
        // Builder
        builder,
        previewLocale,
        // Statuses
        saving,
        generating,
        loadStatus,
        focusedFieldId,
        editingTranslation,
        savingTranslation,
        // Last Updated
        lastUpdated,
        lastUpdatedDisplay,
        lastUpdatedAtDisplay,
        // Readonly
        previewForm,
        focusedField,
        dropdown,
        builderFields,
        copiedField,
        // Actions
        saveForm,
        loadFormById,
        addField,
        updateField,
        removeField,
        copyField,
        pasteField,
        updatePositions,
        transferMetadata,
        hasChanges,
        saveTranslation,
        deleteTranslation,
        generateUpdate,
        // Permissions
        canManageTemplates,
        data_source_items,
    };
});
